import React, { createContext, useEffect } from 'react';
import { useInterpret } from '@xstate/react';
import { appStateMachine } from './appState.ts';
// import { applicationTitleMachine } from './applicationTitle';
// import { breakpointMachine } from './breakpoint';
// import { notebooksMachine } from './notebooks';
// import { notebookModalMachine } from './notebookModal';
// import { notesMachine } from './notes';
import { modalMachine } from './modal';
import { paletteMachine } from './palette';
import interactionLedger from '../../utils/interactionLedger';
import logger from '../../logger';

export const GlobalStateContext = createContext({});

// const {
//   context: { applicationTitle, hasNotes, breakpoint },
// } = appState;
const selectApplicationTitle = (context) => context.applicationTitle;
const selectHasNotes = (context) => context.hasNotes;
const selectBreakpoint = (context) => context.breakpoint;

export const GlobalStateProvider = (props) => {
  console.log('🚀 GlobalStateProvider RENDER (JS) - Component is being called!', {
    hasProps: !!props,
    devTools: props?.devTools,
    timestamp: new Date().toISOString()
  });
  
  const { devTools } = props;
  const inspector =
    typeof window !== 'undefined' ? window.xstateInspector : undefined;

  // Track when GlobalStateProvider mounts/unmounts to verify it doesn't remount unnecessarily
  useEffect(() => {
    try {
      interactionLedger.log(
        'componentMount',
        'appState',
        { component: 'GlobalStateProvider' },
        'GlobalStateProvider mounted - state machine initialized'
      );
    } catch (error) {
      console.error('Failed to log to interaction ledger', error);
    }
    
    return () => {
      try {
        interactionLedger.log(
          'componentUnmount',
          'appState',
          { component: 'GlobalStateProvider' },
          'GlobalStateProvider unmounted - WARNING: This should not happen during normal operation'
        );
      } catch (error) {
        console.error('Failed to log to interaction ledger', error);
      }
    };
  }, []);

  let appState;
  try {
    appState = useInterpret(appStateMachine, {
      devTools,
      inspector,
    });
    console.log('✅ useInterpret called successfully', {
      appStateExists: !!appState,
      hasGetSnapshot: appState ? typeof appState.getSnapshot === 'function' : false
    });
  } catch (error) {
    console.error('❌ CRITICAL: Failed to create appState machine!', error);
    throw error; // Re-throw to prevent app from running in broken state
  }
  const modalState = useInterpret(modalMachine, {
    devTools,
    inspector,
  });
  const paletteState = useInterpret(paletteMachine, {
    devTools,
    inspector,
  });

  // Immediate check to verify state machine is running
  useEffect(() => {
    if (appState) {
      const snapshot = appState.getSnapshot();
      console.log('🔍 XState Status Check:', {
        isRunning: appState.getSnapshot !== undefined,
        currentState: snapshot.value,
        status: snapshot.status,
        contextKeys: Object.keys(snapshot.context || {}).length
      });
      
      // Subscribe to state changes to see if it's actually running
      const subscription = appState.subscribe((state) => {
        console.log('📊 XState State Change:', {
          value: state.value,
          status: state.status,
          changed: state.changed
        });
      });
      
      return () => {
        subscription.unsubscribe();
      };
    }
  }, [appState]);

  // Log state machine initialization
  useEffect(() => {
    if (appState) {
      try {
        const initialState = appState.getSnapshot();
        logger.info({ initialState: initialState.value }, 'State machine initialized and started');
        console.log('✅ XState State Machine Initialized!', {
          state: initialState.value,
          context: initialState.context
        });
        
        try {
          interactionLedger.log(
            'stateMachineInitialized',
            'appState',
            { initialState: initialState.value },
            `State machine initialized: ${JSON.stringify(initialState.value)}`
          );
        } catch (ledgerError) {
          console.error('Failed to log to interaction ledger', ledgerError);
        }
      } catch (error) {
        console.error('Failed to log state machine initialization', error);
      }
    }
  }, [appState]);

  // Expose appState and modalState to window for testing and debugging
  useEffect(() => {
    if (typeof window !== 'undefined' && appState) {
      window.DRY = window.DRY || {};
      window.DRY.appState = appState;
      window.DRY.modalState = modalState;
      console.log('✅ appState exposed to window.DRY.appState', {
        appStateExists: !!appState,
        hasGetSnapshot: typeof appState.getSnapshot === 'function',
        currentState: appState.getSnapshot().value
      });
    } else {
      console.warn('⚠️ Cannot expose appState - window or appState not available', {
        hasWindow: typeof window !== 'undefined',
        hasAppState: !!appState
      });
    }
  }, [appState]);
  // const applicationTitle = useInterpret(applicationTitleMachine);
  // const breakpoint = useInterpret(breakpointMachine);
  // const notebooks = useInterpret(notebooksMachine);
  // const notebookModal = useInterpret(notebookModalMachine);
  // const notes = useInterpret(notesMachine);

  return (
    <GlobalStateContext.Provider
      value={{
        appState,
        // applicationTitle,
        // breakpoint,
        // notebooks,
        // notebookModal,
        // notes,
        modalState,
        paletteState,
        // dryChromeState,
      }}
    >
      {props.children}
    </GlobalStateContext.Provider>
  );
};
